import {
  signal,
  inject,
  OnInit,
  ViewChild,
  Component,
  AfterViewInit,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import {
  injectDirtyCloseHandler,
  DialogCloseReason,
  DialogCloseOptions,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
  SuiDialogConfig,
} from '@sui/dialog';

import { LoggerService } from '@services/logger.service';

import { MoveCopySameFeedComponent } from './move-copy-same-feed/move-copy-same-feed.component';
import { MoveCopyOtherFeedsComponent } from './move-copy-other-feeds/move-copy-other-feeds.component';
import { MoveCopySameDashboardComponent } from './move-copy-same-dashboard/move-copy-same-dashboard.component';
import { MoveCopyOtherDashboardsComponent } from './move-copy-other-dashboards/move-copy-other-dashboards.component';

import { NomObjectClass } from '../../../core/constants';
import { DataObject } from '../../../core/models/core.models';
import { WidgetOperation } from '../../../core/models/widget-operations';
import { SimpleDialogBase } from '../../../shared/abstract/simple-dialog-base';
import { SuiSelectComponent, SuiSelectOptionComponent } from '@sui/select';
import { FormsModule } from '@angular/forms';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

export type WidgetMoveAction = 'copy' | 'move';
export type MoveMode =
  | 'same-dashboard'
  | 'same-feed'
  | 'other-dashboards'
  | 'other-feeds';

interface ModeOption {
  value: MoveMode;
  label: string;
}

export interface MoveCopyWidgetDialogInput {
  widgets: DataObject[];
  currentObject?: DataObject;
}

export interface MoveCopyWidgetDialogOutput {
  data: WidgetOperation[];
}

@Component({
  selector: 'app-move-copy-widget-dialog',
  templateUrl: './move-copy-widget-dialog.component.html',
  styleUrl: './move-copy-widget-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiSelectComponent,
    FormsModule,
    SuiSelectOptionComponent,
    SuiButtonComponent,
    SuiDialogContentSlot,
    MoveCopySameDashboardComponent,
    MoveCopySameFeedComponent,
    forwardRef(() => MoveCopyOtherDashboardsComponent),
    forwardRef(() => MoveCopyOtherFeedsComponent),
    SuiDialogFooterSlot,
    TranslatePipe,
  ],
})
export class MoveCopyWidgetDialogComponent
  extends SimpleDialogBase<
    MoveCopyWidgetDialogInput,
    MoveCopyWidgetDialogOutput
  >
  implements OnInit, AfterViewInit
{
  static dialogConfig: Partial<SuiDialogConfig> = { width: 800, overflow: 'visible' };

  protected override dirtyHandler = injectDirtyCloseHandler(this.dialogRef);

  @ViewChild(MoveCopySameDashboardComponent)
  sameDashboardForm?: MoveCopySameDashboardComponent;
  @ViewChild(MoveCopySameFeedComponent)
  sameFeedForm?: MoveCopySameFeedComponent;
  @ViewChild(MoveCopyOtherDashboardsComponent)
  otherDashboardsForm?: MoveCopyOtherDashboardsComponent;
  @ViewChild(MoveCopyOtherFeedsComponent)
  otherFeedsForm?: MoveCopyOtherFeedsComponent;

  selectedMode: MoveMode = 'same-dashboard';
  availableModes: ModeOption[] = [];
  isDashboardContext = false;

  protected isDirty = signal(false);
  protected isValid = signal(false);

  private logger = inject(LoggerService);

  get widgets(): DataObject[] {
    return this.dialogData.widgets;
  }

  get currentObject(): DataObject | undefined {
    return this.dialogData.currentObject;
  }

  ngOnInit(): void {
    this.determineModes();
  }

  ngAfterViewInit(): void {
    this.setupDirtyHandler(this.isDirty, this.isValid);
  }

  private determineModes(): void {
    this.isDashboardContext =
      this.currentObject?.object_class_id === NomObjectClass.DASHBOARD;

    if (this.isDashboardContext) {
      this.availableModes = [
        {
          value: 'same-dashboard',
          label: this.translateService.instant(
            'move-copy-widget-dialog.label.same-dashboard',
          ),
        },
        {
          value: 'other-dashboards',
          label: this.translateService.instant(
            'move-copy-widget-dialog.label.other-dashboards',
          ),
        },
        {
          value: 'other-feeds',
          label: this.translateService.instant(
            'move-copy-widget-dialog.label.other-feeds',
          ),
        },
      ];
      this.selectedMode = 'same-dashboard';
    } else {
      // ! Feed context
      this.availableModes = [
        {
          value: 'same-feed',
          label: this.translateService.instant(
            'move-copy-widget-dialog.label.same-feed',
          ),
        },
        {
          value: 'other-feeds',
          label: this.translateService.instant(
            'move-copy-widget-dialog.label.other-feeds',
          ),
        },
        {
          value: 'other-dashboards',
          label: this.translateService.instant(
            'move-copy-widget-dialog.label.other-dashboards',
          ),
        },
      ];
      this.selectedMode = 'same-feed';
    }
  }

  onModeChange(mode: MoveMode): void {
    this.selectedMode = mode;
  }

  onFormValidityChange(isValid: boolean): void {
    this.isValid.set(isValid);

    if (isValid) {
      if (this.selectedMode === 'same-feed') {
        this.isDirty.set(false);
        return;
      }

      this.isDirty.set(true);
    }
  }

  protected override performSave(): DialogCloseOptions<MoveCopyWidgetDialogOutput> {
    const formData = this.getFormData();

    this.logger.info(
      `[MoveCopyWidgetDialog] Confirming action in mode: ${this.selectedMode}`,
      formData,
    );

    return {
      reason: DialogCloseReason.Save,
      data: { data: formData! },
    };
  }

  save(): void {
    const formData = this.getFormData();

    this.logger.info(
      `[MoveCopyWidgetDialog] Confirming action in mode: ${this.selectedMode}`,
      formData,
    );

    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: { data: formData },
    });
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }

  private getFormData(): WidgetOperation[] | null {
    switch (this.selectedMode) {
      case 'same-dashboard':
        return this.sameDashboardForm?.getFormData() ?? null;
      case 'same-feed':
        return this.sameFeedForm?.getFormData() ?? null;
      case 'other-dashboards':
        return this.otherDashboardsForm?.getFormData() ?? null;
      case 'other-feeds':
        return this.otherFeedsForm?.getFormData() ?? null;
    }
  }
}
